import {
  Product,
  Store,
  EconomicsResult,
  ChannelIssue,
  ProductIssueReport,
  StoreIssueReport,
  IssueSummary
} from './types';

export function formatMoney(num: number | string | undefined): string {
  return '$' + Number(num || 0).toFixed(2);
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export function showToast(msg: string): void {
  const toast = document.getElementById('toast');
  if (!toast) return;
  toast.textContent = msg;
  toast.classList.add('visible');
  clearTimeout(window.__toastTimer);
  window.__toastTimer = setTimeout(() => {
    toast.classList.remove('visible');
  }, 3200);
}

export function getAmazonProductUrl(product: Product | null | undefined, defaultSellerId = 'A3R7XJXJW7FKHB'): string {
  if (!product) return 'https://www.amazon.com';
  const listing = product.amazonListing || {};
  const asin = listing.asin || product.asin || '';
  const sellerId = listing.sellerId || defaultSellerId;
  if (asin) {
    return `https://www.amazon.com/dp/${encodeURIComponent(asin)}?m=${encodeURIComponent(sellerId)}`;
  }
  return `https://www.amazon.com/s?k=${encodeURIComponent(listing.title || product.title)}&me=${encodeURIComponent(sellerId)}`;
}

export function getSellerCentralSkuUrl(product: Product | null | undefined): string {
  if (!product) return 'https://sellercentral.amazon.com';
  const listing = product.amazonListing || {};
  const sku = listing.amazonSku || product.sku || '';
  return `https://sellercentral.amazon.com/skucentral?mSku=${encodeURIComponent(sku)}`;
}

export function computeEconomics(
  product: Product,
  activeStore: Store | null = null,
  overrides: Partial<{
    alibabaFobUnit: number;
    ddpShippingUnit: number;
    amazonRetailPrice: number;
    ppcEstUnit: number;
    batchSize: number;
  }> = {}
): EconomicsResult {
  const pricing = product.pricing || { alibabaFobUnit: 6.5, ddpShippingUnit: 1.5, amazonRetailPrice: 39.99, ppcEstUnit: 2.5 };
  const alibabaFobUnit = overrides.alibabaFobUnit ?? pricing.alibabaFobUnit ?? 6.5;
  const ddpShippingUnit = overrides.ddpShippingUnit ?? pricing.ddpShippingUnit ?? 1.5;
  const amazonRetailPrice = overrides.amazonRetailPrice ?? pricing.amazonRetailPrice ?? 39.99;
  const referralRate = activeStore ? activeStore.referralRate : (pricing.amazonReferralRate ?? 0.15);
  const fulfillmentFee = activeStore
    ? (activeStore.avgFulfillmentFee || activeStore.fulfillmentFee || 4.75)
    : (pricing.fbaFulfillmentFee ?? 4.75);
  const ppcEstUnit = overrides.ppcEstUnit ?? pricing.ppcEstUnit ?? 2.5;
  const batchSize = overrides.batchSize ?? (product.alibabaSupplier?.moq || 150);

  if (typeof window.calculateFbaEconomics === 'function') {
    return window.calculateFbaEconomics({
      alibabaFobUnit,
      ddpShippingUnit,
      amazonRetailPrice,
      referralRate,
      fulfillmentFee,
      ppcEstUnit,
      batchSize
    });
  }

  const landedUnitCost = +(alibabaFobUnit + ddpShippingUnit).toFixed(2);
  const referralFee = +(amazonRetailPrice * referralRate).toFixed(2);
  const totalAmazonFees = +(referralFee + fulfillmentFee).toFixed(2);
  const netProfitPerUnit = +(amazonRetailPrice - landedUnitCost - totalAmazonFees - ppcEstUnit).toFixed(2);
  const netMarginPct = +((netProfitPerUnit / amazonRetailPrice) * 100).toFixed(1);
  const roiPct = +((netProfitPerUnit / (landedUnitCost || 1)) * 100).toFixed(1);

  return {
    alibabaFobUnit,
    ddpShippingUnit,
    landedUnitCost,
    amazonRetailPrice,
    referralFee,
    fbaFulfillmentFee: fulfillmentFee,
    totalAmazonFees,
    ppcEstUnit,
    netProfitPerUnit,
    netMarginPct,
    roiPct,
    batchSize,
    totalBatchInvestment: +(landedUnitCost * batchSize).toFixed(2),
    expectedBatchNetProfit: +(netProfitPerUnit * batchSize).toFixed(2)
  };
}

export interface AmazonStatusMeta {
  status: string;
  label: string;
  badgeClass: string;
  icon: string;
  tone: string;
  subReason: string;
  isActive: boolean;
  isInactive: boolean;
  note: string;
}

export function getAmazonStatusMeta(product: Product | null | undefined): AmazonStatusMeta {
  const listing = product?.amazonListing || {};
  const status = listing.sellerCentralStatus || listing.amazonSyncStatus || 'NOT_LISTED';
  const st = listing.syncState;

  if (st === 'in_sync' && String(listing.amazonLiveStatus || '').includes('BUYABLE')) {
    return {
      status: 'ACTIVE',
      label: 'Active on Amazon',
      badgeClass: 'is-active',
      icon: '✓',
      tone: 'ok',
      subReason: 'Live & buyable on Amazon US',
      isActive: true,
      isInactive: false,
      note: 'Confirmed live and buyable on Amazon.'
    };
  }
  const amzErrors = (product?.channelIssues?.['store-amz-us']?.issues || []).filter(
    (i) => i.severity === 'error' && i.code !== 'LISTING_NOT_FOUND'
  );
  if ((st === 'inactive' || st === 'issues') && amzErrors.length && amzErrors.every((i) => String(i.code) === '100521')) {
    return {
      status: 'REVIEW',
      label: 'Hidden · Amazon review (≤48h)',
      badgeClass: 'is-pending',
      icon: '⏳',
      tone: 'pending',
      subReason: amzErrors[0].message,
      isActive: false,
      isInactive: true,
      note: 'Amazon has hidden this new listing while it reviews it (error 100521). It publishes automatically unless Amazon asks for more info.'
    };
  }
  if ((st === 'inactive' || st === 'issues') && amzErrors.length) {
    const brand = amzErrors.some((i) => /brand|Transparency/i.test(i.message));
    const imageSuppressed = amzErrors.some(
      (i) => String(i.code) === '18027' || String(i.code) === '100581' || /main image/i.test(i.message)
    );
    return {
      status: 'BLOCKED',
      label: imageSuppressed ? 'Search suppressed · main image' : brand ? 'Blocked · brand approval' : 'Suppressed by Amazon',
      badgeClass: 'is-pending',
      icon: '⛔',
      tone: 'danger',
      subReason: amzErrors[0].message,
      isActive: false,
      isInactive: true,
      note: `Amazon suppressed this listing: ${amzErrors[0].message}`
    };
  }
  if (st === 'inactive' || st === 'issues' || (st === 'in_sync' && listing.amazonLiveStatus)) {
    return {
      status: 'INACTIVE',
      label: 'On Amazon · Not buyable',
      badgeClass: 'is-pending',
      icon: '●',
      tone: 'pending',
      subReason: `Amazon status: ${listing.amazonLiveStatus || 'DISCOVERABLE'}. No stock available to sell yet.`,
      isActive: false,
      isInactive: true,
      note: 'Listing exists on Amazon but has no sellable stock (FBA inventory not received yet).'
    };
  }
  if (st === 'pending') {
    return {
      status: 'SUBMITTED',
      label: 'Submitted · awaiting Amazon',
      badgeClass: 'is-pending',
      icon: '⏳',
      tone: 'pending',
      subReason: listing.amazonStatusNote || 'Waiting for Amazon to create the listing',
      isActive: false,
      isInactive: false,
      note: listing.amazonStatusNote || 'Submitted. Waiting for Amazon to create the listing.'
    };
  }
  if (st === 'push_failed') {
    return {
      status: 'ERROR',
      label: 'Sync failed',
      badgeClass: 'is-pending',
      icon: '!',
      tone: 'danger',
      subReason: listing.amazonStatusNote || 'Amazon rejected the last push',
      isActive: false,
      isInactive: false,
      note: listing.amazonStatusNote || 'Amazon rejected the last push.'
    };
  }
  if (listing.syncState === 'drift') {
    const d = (listing.syncDiffs || [])[0];
    return {
      status: 'DRIFT',
      label: 'Out of sync',
      badgeClass: 'is-pending',
      icon: '↻',
      tone: 'pending',
      subReason: d ? `${d.field}: here ${d.local} vs Amazon ${d.amazon}` : 'Differs from Amazon',
      isActive: false,
      isInactive: false,
      note: 'Local data differs from Amazon. Press Sync to push your values.'
    };
  }
  if (status === 'ACTIVE' || status === 'LIVE' || listing.amazonSyncStatus === 'LIVE') {
    return {
      status: 'ACTIVE',
      label: 'Active on Amazon',
      badgeClass: 'is-active',
      icon: '✓',
      tone: 'ok',
      subReason: 'Live & Buyable on Amazon US',
      isActive: true,
      isInactive: false,
      note: listing.amazonStatusNote || 'Active and buyable on Amazon.'
    };
  }
  if (status === 'ERROR' || listing.amazonSyncStatus === 'ERROR') {
    return {
      status: 'ERROR',
      label: 'Sync failed',
      badgeClass: 'is-pending',
      icon: '!',
      tone: 'danger',
      subReason: listing.amazonStatusNote || 'Amazon rejected the last push',
      isActive: false,
      isInactive: false,
      note: listing.amazonStatusNote || 'Amazon rejected the last push. Open the issues for details.'
    };
  }
  if (status === 'SUBMITTED' || listing.amazonSyncStatus === 'SUBMITTED') {
    return {
      status: 'SUBMITTED',
      label: 'Submitted · awaiting Amazon',
      badgeClass: 'is-pending',
      icon: '⏳',
      tone: 'pending',
      subReason: 'Awaiting catalog propagation',
      isActive: false,
      isInactive: false,
      note: 'Submitted to Seller Central. Ingestion underway.'
    };
  }
  return {
    status: 'NOT_LISTED',
    label: 'Ready to List',
    badgeClass: 'is-ready',
    icon: '⚡',
    tone: 'ready',
    subReason: 'Configured for Amazon GTIN Exemption',
    isActive: false,
    isInactive: false,
    note: 'Ready to sync to Seller Central.'
  };
}

export function buildAmazonListingTsv(products: Product | Product[], activeStore: Store | null = null): string {
  const list = Array.isArray(products) ? products : [products];
  const headers = [
    'SKU',
    'Product Name',
    'Brand',
    'Manufacturer',
    'Standard Price',
    'Quantity',
    'Fulfillment Channel',
    'Condition Type',
    'Item Type Keyword',
    'Main Image URL',
    'Bullet Point 1',
    'Bullet Point 2',
    'Bullet Point 3',
    'Bullet Point 4',
    'Bullet Point 5',
    'Generic Keywords',
    'Product Description'
  ];
  const rows = list.map((p) => {
    const econ = computeEconomics(p, activeStore);
    const listing = p.amazonListing || {};
    const bullets = listing.bullets || [];
    const sku = listing.amazonSku || `AUR-${p.id.toUpperCase()}-FBA`;
    const clean = (val: unknown) => String(val || '').replace(/[\t\r\n]+/g, ' ').trim();
    const title = clean(listing.title || p.title);
    const imageUrl = `https://aurelius-stores-hub-2026.web.app${p.images?.[0] || '/images/fba-prod-01-1.jpg'}`;
    return [
      clean(sku),
      title,
      'Aurelius',
      'AiSmart LLC',
      econ.amazonRetailPrice,
      econ.batchSize || 100,
      'AMAZON_NA',
      'New',
      clean((p.niche || 'home-improvement').toLowerCase().replace(/\s+/g, '-')),
      imageUrl,
      clean(bullets[0] || ''),
      clean(bullets[1] || ''),
      clean(bullets[2] || ''),
      clean(bullets[3] || ''),
      clean(bullets[4] || ''),
      clean(listing.backendKeywords || 'aurelius luxury home office travel essentials'),
      clean(`${title}. Engineered by Aurelius with certified quality standards for Amazon Prime and multi-store e-commerce.`)
    ].join('\t');
  });
  return `${headers.join('\t')}\n${rows.join('\n')}\n`;
}

export function mergeServerProducts(serverProducts: Product[]): Product[] {
  const seedMap = new Map((window.AURELIUS_SEED?.products || []).map((sp) => [sp.id, sp]));
  return serverProducts
    .filter((p) => !String(p.id).startsWith('fba-live-'))
    .map((p) => {
      const seedProd = seedMap.get(p.id) || ({} as Partial<Product>);
      const rawImages = Array.isArray(p.images) && p.images.length ? p.images : seedProd.images || ['/images/fba-prod-01-1.jpg'];
      return {
        ...seedProd,
        ...p,
        amazonListing: { ...(seedProd.amazonListing || {}), ...(p.amazonListing || {}) },
        researchDossier: { ...(seedProd.researchDossier || {}), ...(p.researchDossier || {}) },
        images: rawImages.map((u) => String(u).replace(/(\/images\/fba-prod-\d+-\d+)\.svg$/i, '$1.jpg'))
      } as Product;
    });
}

const CHANNEL_RULES: Record<
  string,
  { label: string; titleMin?: number; titleMax?: number; minImages: number; recommendedImages?: number; minBullets?: number; keywordBytesMax?: number }
> = {
  amazon: { label: 'Amazon', titleMax: 200, minImages: 1, recommendedImages: 6, minBullets: 3, keywordBytesMax: 249 },
  shopify: { label: 'Shopify', titleMax: 255, minImages: 1, recommendedImages: 3 },
  walmart: { label: 'Walmart', titleMax: 199, minImages: 1, recommendedImages: 4, minBullets: 3 },
  tiktok: { label: 'TikTok Shop', titleMin: 25, titleMax: 255, minImages: 1, recommendedImages: 5 },
  ebay: { label: 'eBay', titleMax: 80, minImages: 1, recommendedImages: 4 }
};

const STOP_WORDS = new Set([
  'with', 'for', 'and', 'the', 'from', 'that', 'this', 'your', 'into', 'pack', 'set', 'new', 'inch', 'black', 'white', 'grey', 'gray'
]);

function significantWords(text: string): string[] {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 4 && !STOP_WORDS.has(w) && !/^\d+$/.test(w));
}

export function preflightIssuesClient(product: Product, store: Store): ChannelIssue[] {
  const rules = CHANNEL_RULES[store.channelKey] || CHANNEL_RULES.shopify;
  const listing = product.amazonListing || {};
  const title = String(listing.title || product.title || '').trim();
  const bullets = Array.isArray(listing.bullets) ? listing.bullets.filter(Boolean) : [];
  const images = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  const price = Number(product.pricing?.amazonRetailPrice || 0);
  const out: ChannelIssue[] = [];

  if (!title) {
    out.push({ severity: 'error', code: 'TITLE_MISSING', message: 'Product title is empty.', source: 'preflight' });
  } else {
    if (rules.titleMax && title.length > rules.titleMax) {
      out.push({
        severity: 'error',
        code: 'TITLE_TOO_LONG',
        message: `Title is ${title.length} characters; ${rules.label} allows ${rules.titleMax}.`,
        source: 'preflight',
        field: 'title'
      });
    }
    if (rules.titleMin && title.length < rules.titleMin) {
      out.push({
        severity: 'error',
        code: 'TITLE_TOO_SHORT',
        message: `Title is ${title.length} characters; ${rules.label} requires at least ${rules.titleMin}.`,
        source: 'preflight',
        field: 'title'
      });
    }
  }

  if (!(price > 0)) {
    out.push({ severity: 'error', code: 'PRICE_MISSING', message: 'Retail price is missing or zero.', source: 'preflight', field: 'price' });
  }

  if (images.length < rules.minImages) {
    out.push({
      severity: 'error',
      code: 'IMAGES_MISSING',
      message: `${rules.label} requires at least ${rules.minImages} product image.`,
      source: 'preflight',
      field: 'images'
    });
  } else {
    const uniqueImgs = new Set(images.map((u) => String(u).trim()));
    if (uniqueImgs.size < images.length) {
      out.push({
        severity: 'error',
        code: 'DUPLICATE_GALLERY_IMAGES',
        message: `Product gallery contains duplicate image URLs (${images.length - uniqueImgs.size} duplicate).`,
        source: 'preflight',
        field: 'images'
      });
    }
    const crossLeak = images.find((u) => {
      const m = String(u).match(/^\/images\/(fba-prod-\d+)-/);
      return m && m[1] !== product.id;
    });
    if (crossLeak) {
      out.push({
        severity: 'error',
        code: 'CROSS_PRODUCT_IMAGE_LEAK',
        message: `Image ${crossLeak} belongs to a different product than ${product.id}.`,
        source: 'preflight',
        field: 'images'
      });
    }
    if (rules.recommendedImages && images.length < rules.recommendedImages) {
      out.push({
        severity: 'warning',
        code: 'FEW_IMAGES',
        message: `Only ${images.length} image(s); ${rules.label} recommends ${rules.recommendedImages}+.`,
        source: 'preflight',
        field: 'images'
      });
    }
  }

  if (rules.minBullets && bullets.length < rules.minBullets) {
    out.push({
      severity: 'warning',
      code: 'FEW_BULLETS',
      message: `Only ${bullets.length} bullet point(s); ${rules.label} recommends ${rules.minBullets}+.`,
      source: 'preflight',
      field: 'bullets'
    });
  }

  if (rules.keywordBytesMax && listing.backendKeywords) {
    const bytes = new TextEncoder().encode(String(listing.backendKeywords)).length;
    if (bytes > rules.keywordBytesMax) {
      out.push({
        severity: 'error',
        code: 'KEYWORDS_TOO_LONG',
        message: `Backend keywords are ${bytes} bytes; ${rules.label} allows ${rules.keywordBytesMax}.`,
        source: 'preflight',
        field: 'backendKeywords'
      });
    }
  }

  if (title && bullets.length) {
    const titleWords = new Set(significantWords(title));
    const bulletWords = new Set(significantWords(bullets.join(' ')));
    const overlap = [...titleWords].filter((w) => bulletWords.has(w));
    const ratio = titleWords.size ? overlap.length / titleWords.size : 1;
    if (titleWords.size >= 3 && overlap.length === 0) {
      out.push({
        severity: 'error',
        code: 'CONTENT_MISMATCH',
        message: 'Bullet points share no key words with the title — they appear to describe a different product. Marketplaces suppress listings like this.',
        source: 'preflight',
        field: 'bullets'
      });
    } else if (titleWords.size >= 3 && ratio < 0.25) {
      out.push({
        severity: 'warning',
        code: 'POSSIBLE_CONTENT_MISMATCH',
        message: `Only ${overlap.length} of ${titleWords.size} key title words appear in the bullets. Check that the bullets describe this exact product.`,
        source: 'preflight',
        field: 'bullets'
      });
    }
  }

  return out;
}

export function buildClientIssueReport(
  product: Product,
  stores: Store[],
  liveIssuesByStore: Record<string, { issues?: ChannelIssue[]; checkedAt?: string; error?: string }> = {}
): ProductIssueReport {
  const assigned = Array.isArray(product.assignedStores) ? product.assignedStores : [];
  const syncIssues = product.channelIssues || {};

  const storeReports: StoreIssueReport[] = assigned
    .map((storeId) => stores.find((s) => s.id === storeId))
    .filter((s): s is Store => Boolean(s))
    .map((store) => {
      const live = liveIssuesByStore[store.id];
      const saved = live
        ? []
        : (syncIssues[store.id]?.issues || []).map((i) => ({ source: 'sync' as const, ...i }));
      const preflight = preflightIssuesClient(product, store);
      const rawIssues = [...(live?.issues || []), ...saved, ...preflight];

      const seen = new Set<string>();
      const issues = rawIssues.filter((i) => {
        const key = `${i.code}:${i.message}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return {
        storeId: store.id,
        storeName: store.name,
        channel: store.channel,
        channelKey: store.channelKey,
        liveChecked: Boolean(live && !live.error),
        liveCheckError: live?.error || null,
        lastSyncAttemptAt: syncIssues[store.id]?.attemptedAt || null,
        errorCount: issues.filter((i) => i.severity === 'error').length,
        warningCount: issues.filter((i) => i.severity === 'warning').length,
        issues
      };
    });

  return {
    productId: product.id,
    productTitle: product.title,
    generatedAt: new Date().toISOString(),
    errorCount: storeReports.reduce((n, s) => n + s.errorCount, 0),
    warningCount: storeReports.reduce((n, s) => n + s.warningCount, 0),
    stores: storeReports
  };
}

export function computeClientIssueSummary(product: Product, stores: Store[]): IssueSummary {
  const report = buildClientIssueReport(product, stores);
  return {
    errorCount: report.errorCount,
    warningCount: report.warningCount,
    storesWithErrors: report.stores.filter((s) => s.errorCount > 0).length
  };
}

const STUCK_AFTER_MIN = 30;
export const SYNC_STAGE_LABEL: Record<string, string> = {
  confirmed: 'Active · Buyable',
  listed: 'Offer indexing',
  review: 'Amazon review',
  drift: 'Pending sync',
  pending: 'Waiting for Amazon',
  stuck: 'Stuck',
  failed: 'Rejected',
  attention: 'Needs attention',
  notsent: 'Not sent'
};

export function formatSyncDiffSummary(p: Product): string {
  const l = p.amazonListing || {};
  const diffs = l.syncDiffs || [];
  if (!diffs.length) return '';
  const parts = diffs.map((d) => {
    if (d.field === 'price') return `Price $${Number(d.amazon).toFixed(2)} → $${Number(d.local).toFixed(2)}`;
    if (d.field === 'quantity') return `Stock ${d.amazon} → ${d.local}`;
    if (d.field === 'title') return 'Title updated';
    if (d.field === 'content') return 'Bullets/Images updated';
    return `${d.field}: ${d.amazon} → ${d.local}`;
  });
  const method = l.recommendedSyncMethod === 'PATCH' || diffs.every((d) => ['price', 'quantity'].includes(d.field)) ? 'Fast PATCH' : 'Full PUT';
  return `${parts.join(' · ')} (${method})`;
}

export function classifySync(p: Product): { stage: string; msg?: string; mins?: number | null } {
  const l = p.amazonListing || {};
  const st = l.syncState;
  const mins = l.lastSyncedAt ? Math.round((Date.now() - new Date(l.lastSyncedAt).getTime()) / 60000) : null;
  const allErrs = (p.channelIssues?.['store-amz-us']?.issues || []).filter(
    (i) => i.severity === 'error' && i.code !== 'LISTING_NOT_FOUND'
  );
  const inReview = allErrs.some((i) => String(i.code) === '100521');
  const errs = allErrs.filter((i) => String(i.code) !== '100521');
  if (st === 'drift') {
    return {
      stage: 'drift',
      msg: formatSyncDiffSummary(p) || 'Local changes waiting to sync to Amazon.'
    };
  }
  if (inReview && !errs.length && (st === 'inactive' || st === 'issues')) return { stage: 'review' };
  if (l.amazonSyncStatus === 'ERROR' || st === 'push_failed') {
    return { stage: 'failed', msg: errs[0]?.message || l.amazonStatusNote || 'Amazon rejected the push.' };
  }
  if (st === 'in_sync') {
    return errs.length
      ? { stage: 'attention', msg: `Accepted, but Amazon reports ${errs.length} blocking issue${errs.length === 1 ? '' : 's'}: ${errs[0].message}` }
      : { stage: 'confirmed' };
  }
  if (st === 'pending') {
    return mins != null && mins >= STUCK_AFTER_MIN
      ? { stage: 'stuck', msg: `Submitted ${mins} min ago and Amazon still hasn't created the listing.` }
      : { stage: 'pending', mins };
  }
  if (st === 'inactive' && !errs.length) {
    return { stage: 'listed' };
  }
  if (st === 'inactive' || st === 'issues') {
    return { stage: 'attention', msg: errs[0]?.message || `On Amazon but not buyable (${l.amazonLiveStatus || 'no status'}).` };
  }
  return { stage: 'notsent' };
}

export function attachDialogLightDismissFallback(dialog: HTMLDialogElement | null): void {
  if (!dialog) return;
  if (!('closedBy' in HTMLDialogElement.prototype)) {
    dialog.addEventListener('click', (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const isDialogContent =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;
      if (isDialogContent) return;
      dialog.close();
    });
  }
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (_) {
    return false;
  }
}

export function buildProductFixPrompt(product: Product, report?: ProductIssueReport | null): string {
  const l = product.amazonListing || {};
  const sku = l.amazonSku || l.genericSku || product.sku || 'UNKNOWN-SKU';
  const asin = l.asin || product.asin || 'PENDING';
  const pType = l.productType || 'SHELF';
  const syncInfo = classifySync(product);

  const rawIssues =
    report?.stores?.flatMap((s) => s.issues.map((i) => ({ ...i, storeName: s.storeName }))) ||
    (product.channelIssues?.['store-amz-us']?.issues || []);

  const issueLines = rawIssues.length
    ? rawIssues
        .map(
          (i) =>
            `  - [${String(i.severity || 'error').toUpperCase()} ${i.code}]${i.field ? ` (Field: ${i.field})` : ''}: ${i.message}`
        )
        .join('\n')
    : `  - [STAGE: ${syncInfo.stage.toUpperCase()}] ${syncInfo.msg || l.amazonStatusNote || 'Needs Amazon SP-API sync verification'}`;

  return [
    `Fix all Amazon SP-API listing issues for Product #${String(product.rank || product.id.slice(-2)).padStart(2, '0')} (${product.id}):`,
    ``,
    `- Product ID: ${product.id}`,
    `- SKU: ${sku}`,
    `- ASIN: ${asin}`,
    `- Product Type: ${pType}`,
    `- Current Sync Stage: ${SYNC_STAGE_LABEL[syncInfo.stage] || syncInfo.stage} (syncState: ${l.syncState || 'unknown'})`,
    `- Current Title: ${product.title}`,
    `- Main Image: ${product.images?.[0] || 'none'}${(product as unknown as { cjImageUrl?: string }).cjImageUrl ? ` (CJ CDN: ${(product as unknown as { cjImageUrl?: string }).cjImageUrl})` : ''}`,
    ``,
    `Reported Issues to Fix:`,
    issueLines,
    ``,
    `Instructions:`,
    `1. Inspect ${product.id} (${sku}) in data/products.json and scripts/amazon-sp-api.js.`,
    `2. Resolve each reported error/warning code above (e.g. supply missing attributes for 8560/18448, clamp dimensions for 100335, align title/image with productType for 100239, or if 990003/8541/18653/100390 conflicts with a locked ASIN, delete the conflicted SKU ${sku} and push on a clean incremented Generic SKU).`,
    `3. Push the updated listing to Amazon SP-API via pushProductToAmazon / patchGenericListingAttributes, update lastPushedHash = computeListingContentHash(p), clear resolved channelIssues in data/products.json and public/seed-data.js, and verify Amazon returns ACCEPTED.`
  ].join('\n');
}

export function buildStagePillFixPrompt(
  stageKey: string,
  stageLabel: string,
  items: { p: Product; stage: string; msg?: string }[]
): string {
  const lines = items.map((item) => {
    const p = item.p;
    const l = p.amazonListing || {};
    const sku = l.amazonSku || l.genericSku || p.sku || 'SKU';
    const asin = l.asin || p.asin || 'no-ASIN';
    const pType = l.productType || 'SHELF';
    const issues = (p.channelIssues?.['store-amz-us']?.issues || [])
      .map((i) => `[${String(i.severity).toUpperCase()} ${i.code}] ${i.message}`)
      .join(' | ');
    return `- #${String(p.rank || p.id.slice(-2)).padStart(2, '0')} · ${p.id} · SKU: ${sku} · ASIN: ${asin} · Type: ${pType} · Title: "${p.title}"\n  Issues: ${issues || item.msg || l.amazonStatusNote || stageLabel}`;
  });

  return [
    `Fix all ${items.length} product(s) currently in the "${stageLabel}" (${stageKey}) pill in data/products.json and Amazon Seller Central:`,
    ``,
    ...lines,
    ``,
    `Instructions:`,
    `1. For each product listed above, resolve all reported Amazon SP-API error/warning codes in data/products.json and scripts/amazon-sp-api.js (e.g. missing attributes 8560/18448, title/item_type_keyword mismatch 100239, dimension limits 100335, or ASIN image/catalog conflicts 990003/8541/18653/100390 by deleting the old SKU and pushing a clean Generic SKU if needed).`,
    `2. Batch-push/patch all ${items.length} repaired products to Amazon SP-API, stamp lastPushedHash = computeListingContentHash(p), update data/products.json and public/seed-data.js, and confirm all ${items.length} products are ACCEPTED.`
  ].join('\n');
}
